import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { KeyRound, Loader2, ShieldAlert, MessageCircle } from "lucide-react";

const PIN_KEY = "sfx_admin_pin_ok";
const SESSION_MS = 30 * 60 * 1000; // 30 minutes

export function isAdminPinValid() {
  try {
    const ts = Number(sessionStorage.getItem(PIN_KEY) || 0);
    return ts > 0 && Date.now() - ts < SESSION_MS;
  } catch {
    return false;
  }
}

const CONTACTS = [
  { name: "CEO (Jacky)", phone: "19179355361", label: "917-935-5361" },
  { name: "Manager · Mona Guillaume", phone: "13219485238", label: "321-948-5238" },
];

export function AdminPinGate({ onUnlock }) {
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const refs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  useEffect(() => {
    refs[0].current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setAt = (i, v) => {
    const val = v.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[i] = val;
    setDigits(next);
    setError(false);
    if (val && i < 3) refs[i + 1].current?.focus();
    if (next.every((d) => d !== "")) submit(next.join(""));
  };

  const onKey = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) refs[i - 1].current?.focus();
  };

  const submit = async (pin) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/pin/verify", { pin });
      if (data.ok) {
        try { sessionStorage.setItem(PIN_KEY, String(Date.now())); } catch { /* ignore */ }
        onUnlock();
        return;
      }
      throw new Error("bad");
    } catch {
      setError(true);
      setDigits(["", "", "", ""]);
      refs[0].current?.focus();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-5" data-testid="admin-pin-gate">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center text-center">
          <div className="h-14 w-14 bg-white flex items-center justify-center mb-5">
            <KeyRound className="h-7 w-7 text-black" />
          </div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Admin Access</h1>
          <p className="text-[#888] text-sm mt-2">Enter your 4-digit security PIN to open the admin panel.</p>
        </div>

        <div className="flex items-center justify-center gap-3 mt-8">
          {digits.map((d, i) => (
            <input
              key={i}
              ref={refs[i]}
              data-testid={`admin-pin-${i}`}
              type="password"
              inputMode="numeric"
              maxLength={1}
              value={d}
              disabled={busy}
              onChange={(e) => setAt(i, e.target.value)}
              onKeyDown={(e) => onKey(i, e)}
              className={`h-16 w-14 text-center text-2xl font-bold rounded-sm bg-[#0F0F0F] border outline-none transition-colors ${error ? "border-[#FF3B30]" : "border-[#222] focus:border-white"}`}
            />
          ))}
        </div>

        {busy && <div className="flex justify-center mt-4"><Loader2 className="h-5 w-5 animate-spin text-[#888]" /></div>}

        {error && (
          <div className="mt-6 border border-[#FF3B30]/30 bg-[#FF3B30]/10 rounded-sm p-4" data-testid="admin-pin-error">
            <div className="flex items-center gap-2 text-[#FF3B30] font-semibold text-sm">
              <ShieldAlert className="h-4 w-4" /> Incorrect PIN
            </div>
            <p className="text-xs text-[#aaa] mt-2 leading-relaxed">
              If you need access to the admin panel, contact the team on WhatsApp:
            </p>
            <div className="grid gap-2 mt-3">
              {CONTACTS.map((c) => (
                <a
                  key={c.phone}
                  href={`https://wa.me/${c.phone}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`admin-pin-whatsapp-${c.phone}`}
                  className="flex items-center justify-between gap-2 rounded-sm border border-[#25D366]/40 bg-[#25D366]/10 px-4 py-3 hover:bg-[#25D366]/20 transition-colors"
                >
                  <span className="flex items-center gap-2 text-sm font-medium">
                    <MessageCircle className="h-4 w-4 text-[#25D366]" /> {c.name}
                  </span>
                  <span className="font-mono-term text-xs text-[#25D366]">{c.label}</span>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
